import { useState, useEffect } from 'react';
import { BRAND, LANGUAGE, LANGUAGE_CODES, localeUrl } from '../config/region';
import { useSearchParams } from 'react-router-dom';
import ParticleBackground from './ParticleBackground';
import SeoHead from './SeoHead';
import { Search, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import { API_URLS } from '../config/api';
import { apiGet } from '../utils/apiUtils';
import { t } from '../i18n';
import NovelCard from './NovelCard';
import './SearchPage.css';
import CustomSelect from './CustomSelect';

const NOVELS_PER_PAGE = 6;

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [nameFilter, setNameFilter] = useState(searchParams.get('name') || '');
const [sortOption, setSortOption] = useState(searchParams.get('sort') || 'liked');
  const [novels, setNovels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [totalNovels, setTotalNovels] = useState(0);

  // Accept explicit values to avoid stale closure on reset/pagination
  const fetchNovels = async (
    page = 1,
    name = nameFilter,
    sort = sortOption,
  ) => {
    setLoading(true);
    setError(null);
    try {
      const offset = (page - 1) * NOVELS_PER_PAGE;
      const params = new URLSearchParams();
      params.append('limit', NOVELS_PER_PAGE.toString());
      params.append('offset', offset.toString());
      if (name) params.append('name_substring', name);
      if (sort) params.append('search_sort', sort);

      // Sync state to URL (replace so back button skips filter steps)
      const urlParams = {};
      if (name) urlParams.name = name;
      if (sort && sort !== 'liked') urlParams.sort = sort;
      if (page > 1) urlParams.page = page.toString();
      setSearchParams(urlParams, { replace: true });
      sessionStorage.setItem('searchReturnUrl', '/search' + window.location.search);

      const url = `${API_URLS.PRODUCTION_NOVELS}?${params.toString()}`;
      const response = await apiGet(url, {}, false, '', true);
      setNovels(response.data.novels || []);
      setTotalNovels(response.data.max_novels || 0);
      setCurrentPage(page);
    } catch (err) {
      setError(t('Ошибка при загрузке новелл'));
      setNovels([]);
      setTotalNovels(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchNovels(1, nameFilter, sortOption);
  };

  const handleReset = () => {
    setNameFilter('');
    setSortOption('liked');
    fetchNovels(1, '', 'liked');
  };

  const totalPages = Math.ceil(totalNovels / NOVELS_PER_PAGE);

  useEffect(() => {
    const initPage = parseInt(searchParams.get('page') || '1', 10);
    const initName = searchParams.get('name') || '';
    const initSort = searchParams.get('sort') || 'liked';
    fetchNovels(initPage, initName, initSort);
  }, []);

  /* Language-specific: the search entry point a German visitor should be sent to is /de/search,
     not the bare root. ({search_term_string} is literal — only ${} interpolates in a template.) */
  const websiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: BRAND,
    url: localeUrl('/'),
    inLanguage: LANGUAGE_CODES[LANGUAGE],
    potentialAction: {
      '@type': 'SearchAction',
      target: { '@type': 'EntryPoint', urlTemplate: `${localeUrl('/search')}?name={search_term_string}` },
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <div className="sp-wrap">
      {/* "/" and "/search" render this same page. Pinning the canonical to /search collapses the
          pair into one indexable URL per language instead of two competing duplicates — and it is
          /search that the sitemap lists. */}
      <SeoHead
        title={t('Каталог новелл')}
        description={t('Каталог интерактивных визуальных новелл с ИИ. Выбирай и играй в новеллы, где персонажи отвечают с помощью искусственного интеллекта.')}
        imageAlt={t('{0} — каталог интерактивных новелл', BRAND)}
        path="/search"
        jsonLd={websiteJsonLd}
      />

      <ParticleBackground />

      {/* Content */}
      <div className="sp-app">

        {/* Header */}
        <div className="sp-header">
          <h1 className="sp-title">{t('Поиск новелл')}</h1>
          {/* Catalogue-level age notice. Novels flagged required_18_age still ask for an explicit
              confirmation before they start; this is the general warning that the catalogue as a
              whole is not uniformly age-appropriate, which that per-novel gate cannot convey. */}
          <p className="sp-age-notice">
            {t('Некоторый контент предназначен только для лиц старше 18 лет.')}
          </p>
        </div>

        {/* Filters */}
        <form className="sp-filters" onSubmit={handleSearch}>
          <div className="sp-field">
            <label className="sp-label">{t('Название')}</label>
            <input
              className="sp-input"
              type="text"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              placeholder={t('Поиск по названию…')}
            />
          </div>
          <div className="sp-field">
            <label className="sp-label">{t('Сортировка')}</label>
            <CustomSelect
              className="sp-select"
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
            >
              <option value="liked">{t('Популярные')}</option>
              <option value="ascending">{t('Старые')}</option>
              <option value="descending">{t('Новые')}</option>
            </CustomSelect>
          </div>
          <div className="sp-actions">
            <button type="submit" className="sp-btn-search">
              <Search size={14} /> {t('Поиск')}
            </button>
            <button type="button" className="sp-btn-reset" onClick={handleReset}>
              <RotateCcw size={14} /> {t('Сбросить')}
            </button>
          </div>
        </form>

        {/* Results */}
        {loading ? (
          <div className="sp-loading">
            <div className="sp-spinner" />
            <span>{t('Загрузка…')}</span>
          </div>
        ) : error ? (
          <div className="sp-error">{error}</div>
        ) : novels.length === 0 ? (
          <div className="sp-empty">{t('Новеллы не найдены')}</div>
        ) : (
          <>
            <div className="sp-grid">
              {novels.map((novel) => (
                <NovelCard key={novel.id} novel={novel} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="sp-pagination">
                <button
                  className="sp-page-btn"
                  onClick={() => fetchNovels(currentPage - 1, nameFilter, sortOption)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="sp-page-info">
                  {currentPage} / {totalPages}
                </span>
                <button
                  className="sp-page-btn"
                  onClick={() => fetchNovels(currentPage + 1, nameFilter, sortOption)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}

        <div className="sp-spacer" />
      </div>

    </div>
  );
};

export default SearchPage;
